import * as React from 'react';
import { styled } from '@mui/material/styles';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  AccordionDetails,
  Typography,
  Box,
  DialogContent,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
} from '@mui/material';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordion from '@mui/material/Accordion';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const Accordion = styled((props) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&:before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <MuiAccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '0.9rem' }} />} {...props} />
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, .05)' : 'rgba(0, 0, 0, .03)',
  flexDirection: 'row',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
  },
}));

const PromotionResultDialog = ({ open, onClose, loading, promotionResults }) => {
  if (!open) return null;
  let titleText = '';
  let titleIcon = '';
  const totalPromotions = Object.keys(promotionResults.equipment).length;
  const failedPromotions = Object.keys(promotionResults.equipment).filter(
    (equipKey) => promotionResults.equipment[equipKey].status === 'error'
  ).length;
  const partialPromotions = Object.keys(promotionResults.equipment).filter(
    (equipKey) => promotionResults.equipment[equipKey].status === 'partial'
  ).length;
  const succeededPromotions = Object.keys(promotionResults.equipment).filter(
    (equipKey) => promotionResults.equipment[equipKey].status === 'ok'
  ).length;

  switch (promotionResults.status) {
    case 'success':
      titleText = 'Promotion Successful';
      titleIcon = <CheckCircleIcon color="success" />;
      break;
    case 'partial':
      titleText = 'Promotion Partial Success';
      titleIcon = <WarningIcon color="warning" />;
      break;
    case 'failure':
      titleText = 'Promotion Failure';
      titleIcon = <ErrorIcon color="error" />;
      break;
    default:
      break;
  }

  function getEquipmentIcon(status) {
    switch (status) {
      case 'ok':
        return <CheckCircleIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <WarningIcon color="warning" />;
    }
  }

  function getPointIcon(status) {
    switch (status) {
      case 'ok':
        return <CheckCircleIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <WarningIcon color="warning" />;
    }
  }

  return (
    <Dialog open={open} maxWidth="md">
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
          {titleIcon}
          <Typography sx={{ paddingLeft: 1 }} variant="h6">
            {titleText}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', minWidth: '600px' }}>
          <Typography component="div" sx={{ fontWeight: 'bold' }}>
            {failedPromotions} Promotion{failedPromotions !== 1 ? 's' : ''} Failed
          </Typography>
          <Typography component="div" sx={{ fontWeight: 'bold' }}>
            {partialPromotions} Partial Promotion{partialPromotions !== 1 ? 's' : ''}
          </Typography>
          <Typography component="div" sx={{ fontWeight: 'bold' }}>
            {succeededPromotions} Promotion{succeededPromotions !== 1 ? 's' : ''} Succeeded
          </Typography>
        </Box>
        <Box style={{ overflowY: 'auto', maxHeight: '300px', height: '300px' }}>
          {Object.keys(promotionResults.equipment).map((equipment) => (
            <Accordion key={equipment} border="1">
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                  {getEquipmentIcon(promotionResults.equipment[equipment].status)}
                  <Typography sx={{ paddingLeft: 1 }}>{equipment}</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <List sx={{ width: '100%' }}>
                  {Object.keys(promotionResults.equipment[equipment].points).map((point) => (
                    <>
                      <ListItem alignItems="flex-start">
                        <ListItemAvatar>
                          {getPointIcon(promotionResults.equipment[equipment].points[point].status)}
                        </ListItemAvatar>
                        <ListItemText
                          primary={point}
                          secondary={promotionResults.equipment[equipment].points[point]?.message}
                        />
                      </ListItem>
                      <Divider variant="inset" component="li" />
                    </>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Typography component="div" sx={{ fontWeight: 'bold' }}>
            {totalPromotions} Total Promotion{totalPromotions !== 1 ? 's' : ''}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions style={{ justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={onClose} color="success" disabled={loading}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PromotionResultDialog;
